import LoadingPage from "@components/Loader/LoadingPage";
import { isMobile, isSafari, isSupportedMobileBrowser } from "@lib/browser";
import { to } from "@lib/utils";
import { AppError, AuthError } from "@models/error";
import { Network } from "@models/network";
import { Wallet } from "@models/wallet";
import * as Sentry from "@sentry/browser";
import {
  getIdToken,
  isSignInWithEmailLink,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { ReactNode, useEffect, useRef, useState } from "react";
import {
  firebaseAuth,
  initFirebaseClient,
  matchUrlPath,
  signIn,
  signInByAuthToken,
  signInByWallet,
  STORAGE_AUTH_EMAIL_KEY,
  STORAGE_AUTH_TOKEN_KEY,
  useUserStore,
} from "./auth/services";
import AuthErrorMessage from "./AuthErrorMessage";
import CreateWalletInstruction from "./CreateWalletInstruction";
import { handlePhantomRedirect, PhantomRedirectAction } from "./wallet";

// const HOST = process.env.NEXT_PUBLIC_HOST || "https://app.moongate.id";
// if (!HOST) throw "Missing NEXT_PUBLIC_HOST";

// const API_HOST = process.env.NEXT_PUBLIC_API_HOST || "";
// if (!API_HOST) throw "Missing NEXT_PUBLIC_API_HOST";

// const API_KEY = process.env.NEXT_PUBLIC_API_KEY || "";
// if (!API_KEY) throw "Missing NEXT_PUBLIC_API_KEY";

// const FIREBASE_PROJECT_ID = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
// if (!FIREBASE_PROJECT_ID) throw "Missing NEXT_PUBLIC_FIREBASE_PROJECT_ID";

// const FIREBASE_API_KEY = process.env.NEXT_PUBLIC_FIREBASE_API_KEY;
// if (!FIREBASE_API_KEY) throw "Missing NEXT_PUBLIC_FIREBASE_API_KEY";

// const FIREBASE_APP_ID = process.env.NEXT_PUBLIC_FIREBASE_APP_ID;
// if (!FIREBASE_APP_ID) throw "Missing NEXT_PUBLIC_FIREBASE_APP_ID";

// const PARTICLE_PROJECT_ID = process.env.NEXT_PUBLIC_PARTICLE_PROJECT_ID || "";
// if (!PARTICLE_PROJECT_ID) throw "Missing NEXT_PUBLIC_PARTICLE_PROJECT_ID";

// const PARTICLE_CLIENT_KEY = process.env.NEXT_PUBLIC_PARTICLE_CLIENT_KEY || "";
// if (!PARTICLE_CLIENT_KEY) throw "Missing NEXT_PUBLIC_PARTICLE_CLIENT_KEY";

// const PARTICLE_APP_ID = process.env.NEXT_PUBLIC_PARTICLE_APP_ID || "";
// if (!PARTICLE_APP_ID) throw "Missing NEXT_PUBLIC_PARTICLE_APP_ID";

const AuthProvider = ({
  appHost,
  apiHost,
  apiKey,
  firebaseProjectId,
  firebaseAPIKey,
  firebaseAppId,
  particleProjectId,
  particleClientKey,
  particleAppId,
  authFallbackPath,
  publicRoutesWithAuth,
  publicRoutesNoAuth,
  children,
}: {
  appHost: string;
  apiHost: string;
  apiKey: string;
  firebaseProjectId: string;
  firebaseAPIKey: string;
  firebaseAppId: string;
  particleProjectId: string;
  particleClientKey: string;
  particleAppId: string;
  authFallbackPath: string;
  publicRoutesWithAuth: RegExp[];
  publicRoutesNoAuth: RegExp[];
  children?: ReactNode;
}) => {
  const router = useRouter();
  const user = useUserStore((state) => state.user);
  const authenticating = useUserStore((state) => state.authenticating);
  const initialized = useUserStore((state) => state.initialized);
  const creatingWallet = useUserStore((state) => state.creatingWallet);
  const supportedBrowser = useUserStore((state) => state.supportedBrowser);
  const [approvePopup, setApprovePopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const publicRoutes = [...publicRoutesNoAuth, ...publicRoutesWithAuth];

  const authenticatingRef = useRef(useUserStore.getState().authenticating);
  const sentEmailLinkRef = useRef(useUserStore.getState().sentEmailLink);
  const firebaseAuthenticatingRef = useRef(
    useUserStore.getState().firebaseAuthenticating
  );

  Sentry.configureScope((scope) => {
    scope.setTag("appHost", appHost);
    scope.setTag("apiHost", apiHost);
    scope.setTag("firebaseProjectId", firebaseProjectId);
    scope.setTag("firebaseAppId", firebaseAppId);
    scope.setTag("particleProjectId", particleProjectId);
    scope.setTag("particleAppId", particleAppId);
  });
  // Subscribe to firebase auth state
  useEffect(() => {
    const init = async () => {
      const _approvePopup = isMobile() || isSafari();
      setApprovePopup(_approvePopup);

      Sentry.addBreadcrumb({
        category: "initialization",
        level: "info",
      });

      console.log("AuthProvider: initialized", {
        appHost,
        apiHost,
        firebaseProjectId,
        firebaseAppId,
        particleProjectId,
        particleAppId,
      });
      if (!apiKey) console.error("Missing apiKey");
      if (!firebaseAPIKey) console.error("Missing firebaseAPIKey");
      if (!particleClientKey) console.error("Missing particleClientKey");

      await initFirebaseClient();
      console.log("AuthProvider: firebase initialized");

      useUserStore.subscribe((state) => {
        authenticatingRef.current = state.authenticating;
        sentEmailLinkRef.current = state.sentEmailLink;
        firebaseAuthenticatingRef.current = state.firebaseAuthenticating;
      });

      onAuthStateChanged(firebaseAuth, async (authUser) => {
        useUserStore.setState({ initialized: true });
        Sentry.addBreadcrumb({
          category: "onAuthStateChanged",
          level: "info",
        });
        if (authenticatingRef.current || sentEmailLinkRef.current) return;
        if (authUser && window.location.search.includes("oobCode")) {
          Sentry.addBreadcrumb({
            category: "onAuthStateChanged:logout",
            level: "info",
          });
          Sentry.setUser({ email: authUser.email || "", authId: authUser.uid });
          console.log(
            "AuthProvider:onAuthStateChanged",
            "Logging out previous user"
          );
          window.localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
          await to(signOut(firebaseAuth));
          return;
        }
        console.log(
          "AuthProvider:onAuthStateChanged",
          authUser,
          user,
          authenticatingRef.current,
          sentEmailLinkRef.current
        );
        useUserStore.setState({ authenticating: true });

        if (!authUser || !authUser.uid || !authUser.email) {
          // sign in by existing auth token
          let authToken = "";
          try {
            authToken = JSON.parse(
              window.localStorage.getItem(STORAGE_AUTH_TOKEN_KEY) || ""
            );
          } catch (error) {
            window.localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
          }

          try {
            if (router.query?.auth)
              authToken = String(router.query.auth) || authToken;
          } catch (error) {}

          if (authToken) {
            console.log("onAuthStateChanged", "sign in by existing auth token");
            await to(signInByAuthToken(authToken));
            setLoading(false);
            return;
          }
          useUserStore.setState({ authenticating: false });
          setLoading(false);
          return;
        }

        const { res: idToken } = await to(getIdToken(authUser));
        if (!idToken) return;
        console.log("onAuthStateChanged", idToken);
        useUserStore.setState({
          authenticating: true,
          idToken,
        });

        const { err } = await to(
          signIn(authUser.uid, idToken, authUser.email, ""),
          0
        );
        if (err) {
          console.error("AuthProvider:onAuthStateChanged:signIn", err);
          setError(err);
        }
        setLoading(false);
      });

      onIdTokenChanged(firebaseAuth, async (authUser) => {
        if (!authUser) return;
        const { res: idToken } = await to(getIdToken(authUser));
        if (!idToken) {
          return;
        }
        // console.log("onIdTokenChanged", idToken);
        useUserStore.setState({ idToken });
      });
    };
    init();
  }, []);

  // Check auth on route changes
  const checkAuth = async (pathname: string) => {
    Sentry.addBreadcrumb({
      category: "checkAuth",
      level: "info",
    });
    Sentry.setTag("url", pathname);
    console.log("AuthProvider:checkAuth", pathname);
    if (authenticatingRef.current) return false;
    [pathname] = pathname.split("?");
    useUserStore.setState({ authenticating: false });
    setLoading(false);
    if (matchUrlPath(pathname, publicRoutesNoAuth)) return true;
    if (user) return true;

    if (
      !matchUrlPath(pathname, publicRoutes) &&
      !authenticatingRef.current &&
      !user
    ) {
      const url =
        authFallbackPath +
        (pathname == "/" ? "" : `/${encodeURIComponent(router.asPath)}`);
      console.log("AuthProvider:checkAuth: Redirecting to ", url);
      router.push(url);
      return false;
    }
    return true;
  };

  // Handle sign in email
  const emailLinkSignIn = async () => {
    Sentry.addBreadcrumb({
      category: "emailLinkSignIn",
      level: "info",
    });
    if (!isSupportedMobileBrowser()) {
      Sentry.captureMessage(AuthError.UNSUPPORTED_BROWSER);
      useUserStore.setState({ supportedBrowser: false });
      return;
    }
    window.localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
    if (firebaseAuth.currentUser) await to(signOut(firebaseAuth));
    if (authenticatingRef.current || firebaseAuthenticatingRef.current) return;
    useUserStore.setState({
      authenticating: true,
      firebaseAuthenticating: true,
    });
    setLoading(true);

    const email =
      decodeURIComponent(String(router.query.email || "")) ||
      window.localStorage.getItem(STORAGE_AUTH_EMAIL_KEY) ||
      "";
    Sentry.setTag("email", email);
    console.log("AuthProvider:emailLinkSignIn", email);

    const href = router.asPath;
    Sentry.setTag("href", href);
    console.log("AuthProvider:emailLinkSignIn", href);

    const redirect = decodeURIComponent(String(router.query.redirect || ""));
    const redirectUrl = new URL(redirect, window.location.origin);
    const redirectQuery = Object.fromEntries(redirectUrl.searchParams) || {};
    const params = new URLSearchParams(router.query as any);
    params.delete("oobCode");
    params.delete("mode");
    params.delete("apiKey");
    params.delete("email");
    params.delete("lang");
    let pathname = router.pathname;
    if (redirect && redirectUrl.pathname != router.pathname) {
      pathname = redirectUrl.pathname;
      params.delete("redirect");
    }
    router.push(
      { pathname, query: { ...Object.fromEntries(params), ...redirectQuery } },
      undefined,
      {
        shallow: true,
      }
    );

    useUserStore.setState({
      authenticating: false,
    });
    console.log("AuthProvider:emailLinkSignIn:signInWithEmailLink");
    const { err } = await to(signInWithEmailLink(firebaseAuth, email, href));
    useUserStore.setState({
      firebaseAuthenticating: false,
    });
    if (err) {
      Sentry.captureException(err);
      console.log(err.code, err.message);
      setError(err.code);
      return;
    }
    console.log(
      "AuthProvider:emailLinkSignIn:signInWithEmailLink",
      "firebase signed in"
    );
    window.localStorage.removeItem(STORAGE_AUTH_EMAIL_KEY);
  };

  // Handle redirect actions
  const handleRedirectAction = async (queries: ParsedUrlQuery) => {
    Sentry.addBreadcrumb({
      category: "handleRedirectAction",
      level: "info",
    });
    const action = queries.action as PhantomRedirectAction;
    if (!action) return;
    setLoading(true);
    try {
      switch (action) {
        case PhantomRedirectAction.PHANTOM_SIGN_IN: {
          useUserStore.setState({ authenticating: true });
          const { err, res } = await to(handlePhantomRedirect(queries));
          if (!res?.data.wallet) throw err;
          const wallet = res.data.wallet;
          if (!wallet) throw AppError.UNKNOWN;
          console.log(wallet);
          await signInByWallet(
            new Wallet({
              network: Network.SOLANA,
              address: wallet,
            })
          );
          break;
        }
      }
    } catch (error) {
      console.log("handleRedirectAction", error);
    }
    useUserStore.setState({ authenticating: false });
    setLoading(false);
    return;
  };

  useEffect(() => {
    console.log("AuthProvider", router.pathname, router.query, user);
    if (
      !router.isReady ||
      !initialized ||
      authenticatingRef.current ||
      firebaseAuthenticatingRef.current
    )
      return;
    setLoading(true);
    useUserStore.setState({ authenticating: true });

    // Redirect if authenticated
    if (user) {
      if (router.query.redirect) {
        const redirect = decodeURIComponent(String(router.query.redirect));
        Sentry.setTag("redirect", redirect);
        console.log("AuthProvider: user ready, redirecting to", redirect);
        router.push(redirect);
      } else if (
        authFallbackPath != "/" &&
        router.pathname.includes(authFallbackPath)
      ) {
        console.log("AuthProvider: user ready, redirecting to", "/");
        router.push("/");
      }
      useUserStore.setState({ authenticating: false });
      setLoading(false);
      return;
    }

    useUserStore.setState({ authenticating: false });
    // Handle sign in email
    if (router.query.oobCode && router.query.mode == "signIn") {
      console.log("AuthProvider:isSignInWithEmailLink", window.location.href);
      if (!isSignInWithEmailLink(firebaseAuth, window.location.href)) {
        console.log("AuthProvider:isSignInWithEmailLink redirecting to /");
        router.push("/");
        return;
      }
      emailLinkSignIn();
      return;
    }

    // Handle redirect actions
    if (router.query.action) {
      handleRedirectAction(router.query);
      return;
    }

    checkAuth(router.asPath);
  }, [user?.id, initialized, router.isReady, router.pathname, router.query]);

  useEffect(() => {
    if (!supportedBrowser) setError(AuthError.UNSUPPORTED_BROWSER);
  }, [supportedBrowser]);

  if (authenticating && approvePopup && creatingWallet)
    return <CreateWalletInstruction />;
  if (error) return <AuthErrorMessage error={error} />;
  if (loading || authenticating) return <LoadingPage />;
  return <>{children}</>;
};

export default AuthProvider;
